/**
 * Filters Context
 * Manages the selected filters across the application
 */

import React, { createContext, useContext, useState, ReactNode, useMemo, useEffect, useRef, useCallback } from 'react';
import { useMe } from './MeContext';
import { useQuery } from '@tanstack/react-query';
import { DigitalResponse, FiltersResponse, getFiltersApiV3MetricsFiltersGet, TraditionalResponse } from '../config/api';
import axios, { extractAxiosResponse } from '../config/axios.config';
import { InternalAxiosRequestConfig } from 'axios';
import { useMatch } from 'react-router-dom';

// Custom header to track which org was sent with the request
const SENT_ORG_HEADER = 'x-sent-org-id'

function useFiltersState() {
  const isDigitalRoute = useMatch("/dashboard/digital/*")
  const isPostRoute = useMatch("/dashboard/posts")
  const isTraditionalRoute = useMatch("/dashboard/deepdive/*")
  const { me, org: initialOrg, loading: meLoading, error: meError, refetch } = useMe()
  const [selectedOrg, setSelectedOrgState] = useState<number>(parseInt(initialOrg))
  const [selectedBrand, setSelectedBrand] = useState<number|undefined>(undefined);

  const {data: allFilters, isPending: loading, error} = useQuery({
    queryKey: ["v3", "filters", me],
    queryFn: () => extractAxiosResponse(getFiltersApiV3MetricsFiltersGet()),
    enabled: !meLoading && !meError,
  })
  const [selectedFilters, setSelectedFilters] = useState<FiltersResponse[]>([])

  const allOrgs = useMemo(() => me?.orgs || [], [me])
  const allBrands = useMemo(() => me?.current_org_brands || [], [me])
  const routeType = useMemo(() => !!isTraditionalRoute? 'traditional': !!isDigitalRoute || !!isPostRoute? 'digital': 'common', [isDigitalRoute, isTraditionalRoute])

  // Ref is used to give axios interceptors instant access to the current org without waiting for state change
  const orgRef = useRef<number>(parseInt(initialOrg))

  // Memoized setter to change org and invalidate queries
  const setOrg = useCallback((orgId: number) => {
    // The process is like this:
    // - Change the organization
    // - Refetch the /me route to get the new list of brands

    orgRef.current = orgId;
    setSelectedOrgState(orgId)
    refetch()
  }, [])

  type TypeResponse = {'traditional': TraditionalResponse, 'digital': DigitalResponse}
  const defaultTraditionalFilters: TraditionalResponse = {age: [], gender: [], city: [], time: [], nationality: [], sec: []}
  const defaultDigitalFilters: DigitalResponse = {age: [], gender: [], data_source: [], time: [], country: [], channel: []}

  const getFiltersByType = useCallback(<T extends 'digital'|'traditional'>(type: T): TypeResponse[T] => {
    const defaultFilters: TypeResponse = {'traditional': defaultTraditionalFilters, 'digital': defaultDigitalFilters}

    return allFilters
      ?.filter(f => f.type === 'common' || f.type === type)
      .reduce((acc, f) => ({...acc, [f.field]: selectedFilters.find(sf => sf.field === f.field)?.values || []}), defaultFilters[type]) || defaultFilters[type]
  }, [selectedFilters, allFilters])

  const isFilterEnabled = useCallback((filter: string) => {
    if (!allFilters) return false;

    if (routeType === 'common') return true;

    const filterDef = allFilters.find(f => f.field === filter)

    if (!filterDef) return false;

    return ['common', routeType].includes(filterDef.type)
  }, [allFilters, routeType])

  useEffect(() => {
    if (allBrands.length === 0)
      return;
    
    if (!selectedBrand || !allBrands.find(brand => brand.id === selectedBrand)) { // Case when the selectedBrands list is empty
      setSelectedBrand(allBrands[0].id)
    }
  }, [allBrands, selectedBrand])

  // Register interceptors ONCE on mount (use refs to avoid stale closures)
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
      // Always read from ref for the latest org value
      if (orgRef.current) {
        config.headers['x-org-id'] = orgRef.current.toString()
        // Track which org we sent (for response validation)
        config.headers[SENT_ORG_HEADER] = orgRef.current.toString()
      }
      return config
    });

    const responseInterceptor = axios.interceptors.response.use(response => {
      const responseOrg = response.headers['x-org-id']
      const sentOrg = response.config.headers?.[SENT_ORG_HEADER]

      // console.log(`------------ ${response.config.url} -----------`)
      // console.log("responseOrg", responseOrg)
      // console.log("sentOrg", sentOrg)
      // console.log("orgRef.current", orgRef.current)

      // Only sync state if:
      // 1. Backend returned an org
      // 2. The request we sent matches our current selection (not a stale request)
      // 3. Backend returned a DIFFERENT org than we sent (backend override/default)
      if (responseOrg && sentOrg === orgRef.current.toString() && responseOrg !== sentOrg) {
        setOrg(parseInt(responseOrg))
      }

      return response
    })

    return () => {
      axios.interceptors.request.eject(requestInterceptor)
      axios.interceptors.response.eject(responseInterceptor)
    }
  }, []) // Empty deps - register once on mount

  return {
    allOrgs,
    selectedOrg,
    setSelectedOrg: setOrg,
    allBrands,
    selectedBrand,
    setSelectedBrand,
    routeType,
    allFilters,
    selectedFilters,
    setSelectedFilters,
    getFiltersByType,
    isFilterEnabled,
    loading,
    error,
  }
}

export type FiltersContextType = ReturnType<typeof useFiltersState>;
const FiltersContext = createContext<FiltersContextType | undefined>(undefined);


export const FiltersProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const state = useFiltersState();

  return (
    <FiltersContext.Provider value={state}>
      {children}
    </FiltersContext.Provider>
  );
};

export const useFilters = (): FiltersContextType => {
  const context = useContext(FiltersContext);
  if (!context) {
    throw new Error('useDateFilter must be used within a DateFilterProvider');
  }
  return context;
};

export default FiltersContext;
